/**
 * PWA Install Banner - Apple-inspired minimal callout card
 * Opens InstallPostpartnerSheet for platform-specific instructions
 *
 * Design: Compact, clean, subtle blur with clear hierarchy
 * Positioning: Above bottom nav, respects safe areas
 */

import { memo, useState, useCallback, useEffect } from 'react';
import { X, Download, Smartphone } from '@/lib/icons';
import { triggerHaptic } from '@/lib/haptics';
import type { PWAInstallState } from '@/hooks/usePWAInstall';
import { InstallPostpartnerSheet } from './InstallPostpartnerSheet';

interface PWAInstallBannerProps {
  installState: PWAInstallState;
  onInstall?: () => void;
}

export const PWAInstallBanner = memo(function PWAInstallBanner({
  installState,
  onInstall: _onInstall,
}: PWAInstallBannerProps) {
  const [showSheet, setShowSheet] = useState(false);
  const { platform, dismissInstall, recordShown, installMethod, hasNativePrompt: _hasNativePrompt } = installState;

  // Record that banner was shown (for 24h cooldown)
  useEffect(() => {
    recordShown();
  }, [recordShown]);

  const handleOpenSheet = useCallback(() => {
    triggerHaptic();
    setShowSheet(true);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setShowSheet(false);
    // Only apply cooldown if native prompt was dismissed
    // Manual instructions just close
  }, []);

  const handleDismiss = useCallback(() => {
    triggerHaptic();
    dismissInstall();
  }, [dismissInstall]);

  // Get button text based on install method
  const buttonText = installMethod === 'nativePrompt' ? 'Install' : 'Add';
  const subtitle = platform === 'ios'
    ? 'Add to your Home Screen'
    : installMethod === 'nativePrompt'
      ? 'Get the full app experience'
      : 'Add to your Home Screen';

  return (
    <>
      {/* Minimal callout banner - positioned above bottom nav with safe-area awareness */}
      <div
        className="fixed left-4 right-4 z-40 flex justify-center motion-safe:animate-slide-up"
        style={{ bottom: 'calc(var(--bottom-nav-height, 72px) + env(safe-area-inset-bottom, 0px))' }}
      >
        <div
          className="bg-white/95 backdrop-blur-xl text-gray-900 px-4 py-3 rounded-2xl
                     shadow-[0_4px_24px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]
                     max-w-sm w-full flex items-center gap-3
                     border border-gray-100/80"
          role="alert"
          aria-live="polite"
        >
          {/* Icon */}
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            {platform === 'ios' ? (
              <Smartphone className="w-5 h-5 text-primary-600" />
            ) : (
              <Download className="w-5 h-5 text-primary-600" />
            )}
          </div>

          {/* Message - tappable to open sheet */}
          <button
            onClick={handleOpenSheet}
            className="flex-1 min-w-0 text-left focus-visible:outline-none touch-manipulation"
          >
            <p className="font-semibold text-body text-gray-900 tracking-[-0.01em]">
              Add Postpartner
            </p>
            <p className="text-label-sm text-gray-500 mt-0.5 truncate">
              {subtitle}
            </p>
          </button>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handleOpenSheet}
              className="px-3.5 py-2 text-label font-semibold
                         bg-primary-600 text-white rounded-button
                         hover:bg-primary-700 active:scale-[0.97]
                         motion-safe:transition-all motion-safe:duration-150
                         touch-manipulation"
            >
              {buttonText}
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 -mr-1 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100/80
                         active:scale-95 motion-safe:transition-all motion-safe:duration-150
                         touch-manipulation min-w-[40px] min-h-[40px] flex items-center justify-center"
              aria-label="Dismiss install prompt"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Install sheet */}
      <InstallPostpartnerSheet
        isOpen={showSheet}
        onClose={handleCloseSheet}
        installState={installState}
      />
    </>
  );
});

